// pages/analytics/AuthPerformance.jsx — SOUND-590 (epic SOUND-585)
// Internal analyst report builder over presentation.mart_auth_performance
// (Snowflake MART_AUTH_PERFORMANCE -> presentation Postgres).
// Backend: GET /analytics/auth-performance{,/summary,/timeseries,/report,/filters}
//
// The mart stores TWO measures only — attempts and approved — at the grain of
// transaction_date × every dimension below. No rates are stored; auth rate is
// always recomputed from approved / attempts, which is why this page computes it
// client-side too (the server's auth_rate is the same figure, 0–100).
//
// This is deliberately a REPORT BUILDER, not a fixed dashboard: the analyst picks
// the grouping (1–6 dimensions) and we GROUP BY it server-side via a whitelist.
//
// Several dimensions are only trustworthy with a caveat attached, so the caveats
// are rendered next to the dimension the moment it is selected — see CAVEATS
// below. Do not "clean these up" into a docs page; they are load-bearing.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import clsx from "clsx";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth, requireAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import Skeleton from "components/Skeleton";
import ReportChart from "components/analytics/ReportChart";
import {
  BarChart3, CreditCard, CheckCircle, Percent, CalendarRange, Download,
  Info, AlertTriangle, RotateCcw, Filter, Layers, Plus,
} from "lucide-react";

// ─────────────────────────── formatting ───────────────────────────
const fmtInt = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);
const fmtPct2 = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(2)}%`);

// Auth rate is recomputed from the two base measures rather than read off the
// row, matching how the mart is designed (no stored rates). Server `auth_rate`
// is the same number; computing keeps full precision for sorting.
const authRate = (row) => {
  const a = Number(row?.attempts || 0);
  if (!a) return null;
  return (Number(row?.approved || 0) / a) * 100;
};

function cssVar(name, alpha = 1) {
  if (typeof window === "undefined") return "";
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return "";
  const [h, s, l] = raw.split(/\s+/);
  return `hsla(${parseFloat(h)}, ${s}, ${l}, ${alpha})`;
}

const tooltipStyle = () => ({
  background: cssVar("--card", 0.98),
  border: `1px solid ${cssVar("--border", 1)}`,
  borderRadius: 6,
  fontSize: 12,
});

const RANGE_PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "1y", days: 365 },
];

// Calendar-anchored ranges. Not expressible as a day count: converting "MTD" to
// N days at click time would silently query the wrong window once the page is left
// open across midnight on the 1st, while still displaying "MTD".
const CALENDAR_PRESETS = [
  { value: "mtd", label: "MTD", title: "Month to date — 1st of the current month through today" },
  { value: "ytd", label: "YTD", title: "Year to date — Jan 1 of the current year through today" },
];
const CALENDAR_MODES = new Set(CALENDAR_PRESETS.map((p) => p.value));

function isoDaysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

// 'YYYY-MM-DD' for "today" in the calendar the ACTIVE BASIS reads.
//
// The trailing presets (7d/30d/...) can stay on UTC: their end lands on or past the
// last day of data either way, so the window clips harmlessly and both bases return
// the same days. Verified 2026-07-31 during the divergent window.
//
// MTD/YTD cannot. They anchor to a calendar boundary, so a clock that has already
// rolled over moves the anchor to a NEW PERIOD rather than extending by an empty day.
// UTC and ET dates differ for 4h/day (00:00-04:00 UTC = 8pm-midnight ET), and at
// 20:50 ET on Jul 31 a UTC-anchored MTD resolves to Aug 1..Aug 1 — an empty August
// when the user means July (measured: 0 attempts vs 1,354,301). en-CA formats as
// YYYY-MM-DD; no new dependency.
function todayIn(basis) {
  if (basis !== "et") return isoDaysAgo(0);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

// Same shape the API validates against (routes/auth-performance.mjs). Anything that
// fails this is silently ignored server-side and defaults to a trailing 30 days, so
// validate here too rather than quietly querying a different window than displayed.
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// ─────────────────────── domain constants / caveats ───────────────────────

// Rerunner is only enabled for these clients. Everywhere else
// is_rerunner_attempt is STRUCTURALLY 0 — not a real 0% recovery figure.
const RERUNNER_CLIENTS = new Set(["konfyt", "ascent", "primalstorm", "cleansupplements"]);

// Phoenix clients: brand (campaign_category_name) is 0% populated for them, so
// they all collapse into a single UNKNOWN bucket.
const PHOENIX_CLIENTS = ["rkrmedia", "kaizen", "zenith", "hpmg"];

const UNKNOWNS = new Set(["", "UNKNOWN", "unknown", "N/A", "null"]);
const isUnknown = (v) => v === null || v === undefined || UNKNOWNS.has(String(v));

const MAX_GROUP_BY = 6;
const MAX_RENDER_ROWS = 2000;

// `totalGroups` is the true grouped cardinality from the API (COUNT(*) OVER (),
// computed before LIMIT). `rowCount` is only what came back. Before SOUND-600 the
// API returned rowCount = rows.length, so the truncation check compared a number
// against itself and could never fire — grouping by card_bin returned the top 1,000
// of ~19.9k groups with nothing saying so.
const EMPTY_REPORT = { rows: [], rowCount: 0, totalGroups: 0, truncated: false, groupBy: [] };

// Every groupable dimension. `group` drives the picker layout; `caveat` keys into
// CAVEATS and makes the chip carry a marker + surface the note when selected.
const DIMENSIONS = [
  { key: "transaction_date", label: "Date (UTC)", group: "Time", kind: "date" },
  { key: "transaction_date_et", label: "Date (ET)", group: "Time", kind: "date" },

  { key: "company_id", label: "Company", group: "Merchant" },
  { key: "merchant_id", label: "Merchant ID", group: "Merchant", hint: "The key, with company_id." },
  { key: "merchant_name", label: "Merchant Name", group: "Merchant", hint: "Display only — not a key." },

  { key: "brand", label: "Brand", group: "Product", caveat: "brand" },
  { key: "campaign_name", label: "Campaign", group: "Product" },

  { key: "country", label: "Country", group: "Geo" },
  { key: "currency_code", label: "Currency", group: "Geo" },

  { key: "card_bin", label: "Card BIN", group: "Card", caveat: "card_bin" },
  { key: "card_brand", label: "Card Brand", group: "Card" },
  { key: "issuer_name", label: "Issuer", group: "Card", caveat: "issuer_name" },

  { key: "cascade_id", label: "Cascade ID", group: "Cascade", caveat: "cascade" },
  { key: "cascade_source", label: "Cascade Source", group: "Cascade", caveat: "cascade_source" },

  { key: "is_cit", label: "CIT", group: "Attempt flags", kind: "flag" },
  { key: "is_mit", label: "MIT", group: "Attempt flags", kind: "flag" },
  { key: "is_initial", label: "Initial", group: "Attempt flags", kind: "flag", caveat: "cascade_overlap" },
  { key: "is_cascade_attempt", label: "Cascade Attempt", group: "Attempt flags", kind: "flag", caveat: "cascade_overlap" },
  { key: "is_cascade_retry", label: "Cascade Retry", group: "Attempt flags", kind: "flag", caveat: "cascade_overlap" },
  { key: "is_recycle_attempt", label: "Recycle", group: "Attempt flags", kind: "flag" },
  { key: "is_card_switch", label: "Card Switch", group: "Attempt flags", kind: "flag" },
  { key: "is_rerunner_attempt", label: "Rerunner", group: "Attempt flags", kind: "flag", caveat: "rerunner" },
];

const DIM_BY_KEY = Object.fromEntries(DIMENSIONS.map((d) => [d.key, d]));
const DIM_GROUPS = ["Time", "Merchant", "Product", "Geo", "Card", "Cascade", "Attempt flags"];
const FLAG_DIMS = new Set(DIMENSIONS.filter((d) => d.kind === "flag").map((d) => d.key));
const DATE_DIMS = new Set(DIMENSIONS.filter((d) => d.kind === "date").map((d) => d.key));

// The caveats. Each one is a documented way this mart will mislead an analyst who
// reads a number at face value. `tone: warning` = the number is wrong-ish if you
// don't know; `info` = coverage / interpretation.
const CAVEATS = {
  issuer_name: {
    tone: "warning",
    title: "Issuer resolves for ~94% of card attempts — ~59–64% of all attempts",
    body:
      "Issuer comes from our BIN table, so it only exists where a card BIN does. Non-card rails " +
      "(PayPal, Prepaid) have no issuer at all, and Amex + Discover are absent from the BIN table entirely. " +
      "Everything unresolved lands in UNKNOWN — those rows are real attempts, not a gap in the counts. " +
      "Add Card Brand to the grouping to separate “no issuer exists” from “issuer missing”.",
  },
  brand: {
    tone: "warning",
    title: "Brand is CheckoutChamp-only — 100% populated on CC, 0% on Phoenix",
    body:
      `The four Phoenix clients (${PHOENIX_CLIENTS.join(", ")}) have no brand on the source record, so all of ` +
      "their volume falls into one UNKNOWN bucket. An UNKNOWN brand row is not a small residual — for a Phoenix " +
      "client it is that client's entire volume.",
  },
  cascade_source: {
    tone: "warning",
    title: "native = vendor truth. derived = our inference, unverifiable",
    body:
      "native means the cascade flag came straight off CheckoutChamp's own field — it reproduces CC's Cascade " +
      "Performance screen exactly. derived means Phoenix, where there is no vendor cascade field: we inferred it " +
      "(MID change at recycle 0 within 10s) and there is no vendor figure to check it against. Derived rows are " +
      "rendered dimmed + marked below; do not read them at the same confidence as native.",
  },
  cascade: {
    tone: "info",
    title: "Cascade confidence depends on the source",
    body:
      "Cascade identity is vendor-native on CheckoutChamp and derived on Phoenix. Add Cascade Source to the " +
      "grouping so the two are never summed or compared as if equally reliable.",
  },
  cascade_overlap: {
    tone: "warning",
    title: "Cascade Attempt OVERLAPS Initial — the flags do not partition",
    body:
      "is_cascade_attempt matches CheckoutChamp exactly, but CC also stamps the FIRST attempt of a cascade-routed " +
      "order, so it overlaps is_initial. Summing flag buckets double-counts those attempts. is_cascade_retry " +
      "excludes first attempts and partitions cleanly — use it whenever you need the buckets to add up.",
  },
  card_bin: {
    tone: "info",
    title: "27,795 distinct BINs — most BIN × day cells are thin",
    body:
      "Nothing is suppressed and no sample floor is applied (that is a product requirement). But a 1-of-1 row is " +
      "100% auth rate and means nothing, so rates below the low-sample threshold are dimmed and tagged with their " +
      "attempt count. Adjust or disable the threshold in the report header.",
  },
  rerunner: {
    tone: "warning",
    title: "Rerunner is structurally 0 for clients that don't run it",
    body:
      `Only ${[...RERUNNER_CLIENTS].join(", ")} have rerunner enabled. For every other client ` +
      "is_rerunner_attempt = 0 because the feature does not exist there — that is not-applicable, not a real 0%. " +
      "Group by Company to keep the two apart; where we can tell, a No renders as n/a.",
  },
};

// The 8 flag filters, as tri-state. Keys are the API query params.
const FLAG_FILTERS = [
  { key: "isCit", label: "CIT" },
  { key: "isMit", label: "MIT" },
  { key: "isInitial", label: "Initial", note: "Overlaps Cascade Attempt on CheckoutChamp." },
  { key: "isCascadeAttempt", label: "Cascade Attempt", note: "Matches CC exactly, but overlaps Initial — does not partition." },
  { key: "isCascadeRetry", label: "Cascade Retry", note: "Excludes first attempts — partitions cleanly." },
  { key: "isRecycleAttempt", label: "Recycle" },
  { key: "isCardSwitch", label: "Card Switch" },
  { key: "isRerunnerAttempt", label: "Rerunner", note: "Structurally 0 for clients without rerunner enabled." },
];

// Dimension filters. Keys are the API query params.
const EMPTY_DIM_FILTERS = {
  companyId: "", merchantId: "", brand: "", campaignName: "", country: "",
  currencyCode: "", cardBrand: "", issuerName: "", cascadeId: "",
  cardBin: "", binFrom: "", binTo: "",
};
const EMPTY_FLAGS = Object.fromEntries(FLAG_FILTERS.map((f) => [f.key, ""]));

const GROUP_PRESETS = [
  { label: "Merchant × day", dims: ["transaction_date", "merchant_name"] },
  { label: "Cascade", dims: ["cascade_source", "is_cascade_retry"] },
  { label: "Card mix", dims: ["card_brand", "issuer_name"] },
  { label: "BIN × day", dims: ["transaction_date", "card_bin"] },
  { label: "Geo", dims: ["country", "currency_code"] },
  { label: "CIT / MIT", dims: ["company_id", "is_cit", "is_mit"] },
];

// ─────────────────────────── query string ───────────────────────────
function buildQS({ startDate, endDate, dateBasis, dimFilters, flags, groupBy, limit }) {
  const p = new URLSearchParams();
  if (startDate) p.set("startDate", startDate);
  if (endDate) p.set("endDate", endDate);
  if (dateBasis) p.set("dateBasis", dateBasis);
  Object.entries(dimFilters || {}).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) p.set(k, String(v));
  });
  Object.entries(flags || {}).forEach(([k, v]) => {
    if (v === "0" || v === "1") p.set(k, v);
  });
  if (groupBy && groupBy.length) p.set("groupBy", groupBy.join(","));
  if (limit) p.set("limit", String(limit));
  return p.toString();
}

// ─────────────────────────── small UI pieces ───────────────────────────
const CHIP = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1";

function Segmented({ value, onChange, options, size = "md" }) {
  return (
    <div className="inline-flex rounded-lg ring-1 ring-border overflow-hidden">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          aria-pressed={value === o.value}
          title={o.title}
          onClick={() => onChange(o.value)}
          className={clsx(
            size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm",
            value === o.value
              ? "bg-brand/15 text-foreground"
              : "text-muted-foreground hover:bg-muted/60"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Callout({ tone = "info", title, body, action }) {
  const warn = tone === "warning";
  const Icon = warn ? AlertTriangle : Info;
  return (
    <div
      className={clsx(
        "rounded-lg p-3 ring-1 flex gap-3",
        warn ? "bg-warning/10 ring-warning/25" : "bg-info/10 ring-info/25"
      )}
    >
      <Icon className={clsx("h-4 w-4 shrink-0 mt-0.5", warn ? "text-warning" : "text-info")} />
      <div className="min-w-0">
        <p className={clsx("text-xs font-semibold", warn ? "text-warning" : "text-info")}>{title}</p>
        <p className="mt-0.5 text-xs text-foreground/80 leading-relaxed">{body}</p>
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, height = 260 }) {
  return (
    <div className="surface rounded-xl ring-1 ring-border p-5">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────────── cell renderers ───────────────────────────

function UnknownChip({ title }) {
  return (
    <span className={clsx(CHIP, "bg-muted text-muted-foreground ring-border")} title={title}>
      UNKNOWN
    </span>
  );
}

function CascadeSourceChip({ value }) {
  const v = String(value || "").toLowerCase();
  if (v === "derived")
    return (
      <span
        className={clsx(CHIP, "bg-warning/15 text-warning ring-warning/30")}
        title="Phoenix — we inferred the cascade. No vendor figure exists to verify it against."
      >
        <AlertTriangle className="h-3 w-3" /> derived
      </span>
    );
  if (v === "native")
    return (
      <span
        className={clsx(CHIP, "bg-success/15 text-success ring-success/30")}
        title="CheckoutChamp's own cascade field — vendor truth."
      >
        native
      </span>
    );
  return <UnknownChip title="No cascade source on this row." />;
}

// Flags render as Yes / No, except is_rerunner_attempt on a client that has no
// rerunner — that renders n/a, because a structural 0 is not a measured 0.
function FlagChip({ dimKey, value, row }) {
  const truthy = value === true || value === 1 || value === "1" || value === "t" || value === "true";
  if (!truthy && dimKey === "is_rerunner_attempt") {
    const company = String(row?.company_id || "").toLowerCase();
    if (company && !RERUNNER_CLIENTS.has(company)) {
      return (
        <span
          className={clsx(CHIP, "bg-muted text-muted-foreground/70 ring-border italic")}
          title={`${row.company_id} has no rerunner enabled — not applicable, not a measured 0%.`}
        >
          n/a
        </span>
      );
    }
  }
  return truthy ? (
    <span className={clsx(CHIP, "bg-brand/15 text-foreground ring-brand/30")}>Yes</span>
  ) : (
    <span className={clsx(CHIP, "bg-muted text-muted-foreground ring-border")}>No</span>
  );
}

function DimCell({ dimKey, row }) {
  const v = row[dimKey];
  if (FLAG_DIMS.has(dimKey)) return <FlagChip dimKey={dimKey} value={v} row={row} />;
  if (DATE_DIMS.has(dimKey)) return <span className="text-muted-foreground">{String(v ?? "").slice(0, 10) || "—"}</span>;
  if (dimKey === "cascade_source") return <CascadeSourceChip value={v} />;
  if (dimKey === "issuer_name" && isUnknown(v))
    return <UnknownChip title="No issuer resolved — non-card rail, or an Amex/Discover/absent BIN. The attempts still count." />;
  if (dimKey === "brand" && isUnknown(v))
    return <UnknownChip title="No brand on the source record — Phoenix clients have none at all." />;
  if (isUnknown(v)) return <UnknownChip title="Unresolved dimension. The attempts still count." />;
  return (
    <span className="max-w-[240px] truncate inline-block align-bottom" title={String(v)}>
      {String(v)}
    </span>
  );
}

// ─────────────────────────── CSV ───────────────────────────
const csvEscape = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function exportReportCsv({ rows, groupBy, startDate, endDate, dateBasis }) {
  const headers = [...groupBy, "attempts", "approved", "auth_rate_pct"];
  const body = rows.map((r) => [
    ...groupBy.map((k) => {
      const v = r[k];
      if (DATE_DIMS.has(k)) return csvEscape(String(v ?? "").slice(0, 10));
      if (FLAG_DIMS.has(k)) return v === true || v === 1 || v === "1" || v === "t" ? "1" : "0";
      return csvEscape(v);
    }),
    Number(r.attempts || 0),
    Number(r.approved || 0),
    authRate(r) === null ? "" : authRate(r).toFixed(4),
  ]);
  const csv = [headers.join(","), ...body.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `auth-performance_${groupBy.join("-").slice(0, 80)}_${startDate}_${endDate}_${dateBasis}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─────────────────────────── group-by picker ───────────────────────────
function GroupByPicker({ groupBy, onChange }) {
  const atMax = groupBy.length >= MAX_GROUP_BY;

  const toggle = (key) => {
    if (groupBy.includes(key)) onChange(groupBy.filter((k) => k !== key));
    else if (!atMax) onChange([...groupBy, key]);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" /> Group by
          </h3>
          <p className="text-xs text-muted-foreground">
            Pick 1–{MAX_GROUP_BY} dimensions. Order is the grouping order — click a selected chip to drop it.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {GROUP_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => onChange(p.dims)}
              className="px-2 py-1 text-xs rounded-md ring-1 ring-border text-muted-foreground hover:bg-muted/60"
            >
              {p.label}
            </button>
          ))}
          {groupBy.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="px-2 py-1 text-xs rounded-md ring-1 ring-border text-muted-foreground hover:bg-muted/60"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Selected, in grouping order */}
      <div className="flex flex-wrap items-center gap-2 min-h-[2rem]">
        {groupBy.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">Nothing selected — pick a dimension below.</span>
        ) : (
          groupBy.map((k, i) => (
            <button
              key={k}
              type="button"
              onClick={() => toggle(k)}
              title="Remove from grouping"
              className="inline-flex items-center gap-2 rounded-full bg-brand/15 ring-1 ring-brand/40 px-2.5 py-1 text-xs text-foreground"
            >
              <span className="grid h-4 w-4 place-items-center rounded-full bg-brand/30 text-[10px] font-semibold">
                {i + 1}
              </span>
              {DIM_BY_KEY[k]?.label || k}
              {DIM_BY_KEY[k]?.caveat && <AlertTriangle className="h-3 w-3 text-warning" />}
              <span className="text-muted-foreground">×</span>
            </button>
          ))
        )}
        {atMax && (
          <span className="text-[11px] text-warning">
            {MAX_GROUP_BY}-dimension max reached — drop one to add another.
          </span>
        )}
      </div>

      {/* All dimensions, grouped */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
        {DIM_GROUPS.map((g) => (
          <div key={g}>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">{g}</p>
            <div className="flex flex-wrap gap-1.5">
              {DIMENSIONS.filter((d) => d.group === g).map((d) => {
                const selected = groupBy.includes(d.key);
                const disabled = !selected && atMax;
                const title = [d.hint, d.caveat && CAVEATS[d.caveat]?.title].filter(Boolean).join(" — ");
                return (
                  <button
                    key={d.key}
                    type="button"
                    aria-pressed={selected}
                    disabled={disabled}
                    title={title || d.label}
                    onClick={() => toggle(d.key)}
                    className={clsx(
                      "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs ring-1 transition-colors",
                      selected
                        ? "bg-brand/15 ring-brand/40 text-foreground"
                        : disabled
                        ? "ring-border text-muted-foreground/40 cursor-not-allowed"
                        : "ring-border text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    )}
                  >
                    {d.label}
                    {d.caveat && (
                      <AlertTriangle
                        className={clsx("h-3 w-3", CAVEATS[d.caveat]?.tone === "warning" ? "text-warning" : "text-info")}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────── filter panel ───────────────────────────
function FilterPanel({ filters, dimFilters, setDimFilters, flags, setFlags, onReset }) {
  const set = (k) => (e) => setDimFilters((s) => ({ ...s, [k]: e.target.value }));
  const merchantOptions = useMemo(
    () => (filters.merchants || []).filter((m) => !dimFilters.companyId || m.companyId === dimFilters.companyId),
    [filters.merchants, dimFilters.companyId]
  );

  const activeCount =
    Object.values(dimFilters).filter((v) => v !== "").length +
    Object.values(flags).filter((v) => v !== "").length;

  return (
    <div className="surface rounded-xl ring-1 ring-border p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" /> Filters
          {activeCount > 0 && <span className="badge badge-brand">{activeCount} active</span>}
        </h3>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" /> Reset
          </button>
        )}
      </div>

      {/* Dimension filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <label className="block">
          <span className="text-xs text-muted-foreground block mb-1">Company</span>
          <select className="select w-full" value={dimFilters.companyId} onChange={set("companyId")}>
            <option value="">All companies</option>
            {(filters.companies || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground block mb-1">Merchant</span>
          <select className="select w-full" value={dimFilters.merchantId} onChange={set("merchantId")}>
            <option value="">All merchants</option>
            {merchantOptions.map((m) => (
              <option key={`${m.companyId}|${m.merchantId}`} value={m.merchantId}>
                {m.merchantName || m.merchantId} ({m.merchantId})
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground block mb-1">
            Brand <span className="text-warning" title="CheckoutChamp only — Phoenix clients are all UNKNOWN.">⚠</span>
          </span>
          <select className="select w-full" value={dimFilters.brand} onChange={set("brand")}>
            <option value="">All brands</option>
            {(filters.brands || []).map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground block mb-1">Campaign</span>
          <input
            className="input w-full"
            list="ap-campaigns"
            placeholder="All campaigns"
            value={dimFilters.campaignName}
            onChange={set("campaignName")}
          />
          <datalist id="ap-campaigns">
            {(filters.campaigns || []).map((c) => <option key={c} value={c} />)}
          </datalist>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground block mb-1">Country</span>
          <select className="select w-full" value={dimFilters.country} onChange={set("country")}>
            <option value="">All countries</option>
            {(filters.countries || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground block mb-1">Currency</span>
          <select className="select w-full" value={dimFilters.currencyCode} onChange={set("currencyCode")}>
            <option value="">All currencies</option>
            {(filters.currencies || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground block mb-1">Card brand</span>
          <select className="select w-full" value={dimFilters.cardBrand} onChange={set("cardBrand")}>
            <option value="">All card brands</option>
            {(filters.cardBrands || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground block mb-1">
            Issuer{" "}
            <span
              className="text-warning"
              title="Resolves for ~94% of card attempts, ~59–64% of all attempts. Non-card rails and Amex/Discover have none."
            >
              ⚠
            </span>
          </span>
          <input
            className="input w-full"
            list="ap-issuers"
            placeholder="All issuers"
            value={dimFilters.issuerName}
            onChange={set("issuerName")}
          />
          <datalist id="ap-issuers">
            {(filters.issuers || []).map((c) => <option key={c} value={c} />)}
          </datalist>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground block mb-1">Cascade</span>
          <select className="select w-full" value={dimFilters.cascadeId} onChange={set("cascadeId")}>
            <option value="">All cascades</option>
            {(filters.cascades || []).map((c) => (
              <option key={`${c.cascadeId}|${c.cascadeSource}`} value={c.cascadeId}>
                {c.cascadeId} · {c.cascadeSource}
              </option>
            ))}
          </select>
        </label>

        {/* BIN — exact or range */}
        <div className="sm:col-span-2 xl:col-span-3">
          <span className="text-xs text-muted-foreground block mb-1">BIN (exact, or a range)</span>
          <div className="flex flex-wrap items-center gap-2">
            <input
              className="input w-28"
              inputMode="numeric"
              maxLength={6}
              placeholder="411111"
              value={dimFilters.cardBin}
              onChange={(e) => setDimFilters((s) => ({ ...s, cardBin: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
            />
            <span className="text-xs text-muted-foreground">or</span>
            <input
              className="input w-28"
              inputMode="numeric"
              maxLength={6}
              placeholder="from"
              value={dimFilters.binFrom}
              onChange={(e) => setDimFilters((s) => ({ ...s, binFrom: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
            />
            <span className="text-xs text-muted-foreground">→</span>
            <input
              className="input w-28"
              inputMode="numeric"
              maxLength={6}
              placeholder="to"
              value={dimFilters.binTo}
              onChange={(e) => setDimFilters((s) => ({ ...s, binTo: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
            />
            {dimFilters.cardBin && (dimFilters.binFrom || dimFilters.binTo) && (
              <span className="text-[11px] text-warning">
                Exact BIN and range are both set — the server applies the exact value.
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Flags — tri-state */}
      <div>
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Attempt flags</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-2">
          {FLAG_FILTERS.map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-3">
              <span className="text-xs text-foreground/90" title={f.note}>
                {f.label}
                {f.note && <span className="ml-1 text-muted-foreground">ⓘ</span>}
              </span>
              <Segmented
                size="sm"
                value={flags[f.key]}
                onChange={(v) => setFlags((s) => ({ ...s, [f.key]: v }))}
                options={[
                  { value: "", label: "Any" },
                  { value: "0", label: "No" },
                  { value: "1", label: "Yes" },
                ]}
              />
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Cascade Attempt overlaps Initial on CheckoutChamp (CC stamps a cascade-routed order's first attempt too), so
          those two flags do not partition the attempts. Cascade Retry excludes first attempts and does.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────── page ───────────────────────────
function AuthPerformanceInner() {
  const { user } = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();

  const [filters, setFilters] = useState({
    companies: [], merchants: [], brands: [], campaigns: [], countries: [],
    currencies: [], cardBrands: [], issuers: [], cascades: [],
  });

  const [rangeDays, setRangeDays] = useState(30);
  const [dateBasis, setDateBasis] = useState("utc");
  const [dimFilters, setDimFilters] = useState(EMPTY_DIM_FILTERS);
  const [flags, setFlags] = useState(EMPTY_FLAGS);
  const [groupBy, setGroupBy] = useState(["company_id"]);

  const [summary, setSummary] = useState(null);
  const [range, setRange] = useState(null);
  const [byCompany, setByCompany] = useState([]);
  const [series, setSeries] = useState([]);
  const [report, setReport] = useState(EMPTY_REPORT);

  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reportError, setReportError] = useState(false);

  const [sort, setSort] = useState({ key: "attempts", dir: "desc" });
  const [lowSampleFloor, setLowSampleFloor] = useState(30);
  // Defaults on: a chart that has to be found is a chart nobody uses. Dismissible
  // because at 6 dimensions the table is the honest view and the chart is a summary.
  const [showChart, setShowChart] = useState(true);

  // Date range: presets, or an explicit custom window.
  //
  // Presets are always "last N days ending TODAY", which cannot reproduce a
  // CheckoutChamp report window — those have a fixed start AND end. Custom mode pins
  // both ends so the comparison is apples-to-apples. Pair it with the ET basis, since
  // CheckoutChamp reports in Eastern.
  // 'preset' (trailing N days) | 'mtd' | 'ytd' (calendar-anchored) | 'custom'
  const [dateMode, setDateMode] = useState("preset");
  const [customStart, setCustomStart] = useState(() => isoDaysAgo(7));
  const [customEnd, setCustomEnd] = useState(() => isoDaysAgo(0));

  const customValid = useMemo(
    () => DATE_RE.test(customStart) && DATE_RE.test(customEnd) && customStart <= customEnd,
    [customStart, customEnd]
  );

  // rangeDays - 1, because the API's BETWEEN is INCLUSIVE of both ends. isoDaysAgo(7)
  // through today is EIGHT dates, so "7d" was silently returning 8 days of volume
  // (and "1y" 366). That is a real problem for reconciliation: a 7d range compared
  // against a 7-day CheckoutChamp report was a full extra day of attempts.
  const presetStart = useMemo(() => isoDaysAgo(Math.max(rangeDays - 1, 0)), [rangeDays]);
  const presetEnd = useMemo(() => isoDaysAgo(0), []);

  // MTD/YTD resolve against the ACTIVE BASIS's calendar (see todayIn). Recomputed
  // when the basis changes, so flipping UTC<->ET re-anchors the period rather than
  // keeping a window derived from the other calendar.
  const calendarRange = useMemo(() => {
    if (!CALENDAR_MODES.has(dateMode)) return null;
    const today = todayIn(dateBasis);
    const start = dateMode === "mtd" ? `${today.slice(0, 7)}-01` : `${today.slice(0, 4)}-01-01`;
    return { start, end: today };
  }, [dateMode, dateBasis]);

  const useCustom = dateMode === "custom" && customValid;
  const startDate = useCustom ? customStart : calendarRange ? calendarRange.start : presetStart;
  const endDate = useCustom ? customEnd : calendarRange ? calendarRange.end : presetEnd;

  // Inclusive, to match the API's BETWEEN. Surfaced in the UI because "7d" spanning
  // 8 dates is exactly the kind of thing nobody notices until a total won't reconcile.
  const inclusiveDays = useMemo(() => {
    const a = Date.parse(`${startDate}T00:00:00Z`);
    const b = Date.parse(`${endDate}T00:00:00Z`);
    if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
    return Math.round((b - a) / 86400000) + 1;
  }, [startDate, endDate]);

  // Matches EITHER calendar's today. Under UTC both terms are identical, so the
  // trailing presets keep their existing behaviour exactly; under ET this keeps the
  // "today partial" warning firing for an ET-anchored MTD/YTD end date, which UTC's
  // today would not equal during the 4h divergent window.
  const endsToday = endDate === isoDaysAgo(0) || endDate === todayIn(dateBasis);
  const auth = useMemo(() => ({ id: user?.id, accessToken: user?.accessToken }), [user]);

  // Serialize the scope so effects don't refire on object identity alone.
  const scopeQs = useMemo(
    () => buildQS({ startDate, endDate, dateBasis, dimFilters, flags }),
    [startDate, endDate, dateBasis, dimFilters, flags]
  );

  // Filter options — RE-FETCHED whenever the date range or basis changes.
  //
  // Not "once". GET /filters is date-range-scoped server-side (it defaults to a
  // trailing 30 days) because this mart is attempt-aggregate grain — ~4M rows and
  // 27,795 distinct BINs — so nine unbounded DISTINCT scans on every page mount is
  // not viable. Consequence: options CASCADE with the selected range. Fetching once
  // with no dates would pin the pickers to the last 30 days and silently omit
  // values that exist in a wider range the analyst had selected.
  // Include the selected COMPANY so option lists are scoped to it: picking a company
  // must narrow brands / campaigns / countries / MIDs to that company's own. Without
  // it the campaign picker lists all 367 campaigns across every client, which implies
  // a company has campaigns it doesn't. The API deliberately keeps the *company* list
  // itself unscoped so you can still switch away.
  //
  // Only companyId is sent, not the whole dimFilters object: scoping each list by
  // every other selection would make a list narrow itself to its own selection.
  const filtersQs = useMemo(
    () => buildQS({ startDate, endDate, dateBasis, dimFilters: { companyId: dimFilters.companyId } }),
    [startDate, endDate, dateBasis, dimFilters.companyId]
  );

  useEffect(() => {
    if (!user || !effectiveCompanyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await makeApiRequest(
          "get",
          `${apiRoutes.analyticsAuthPerformanceFilters}?${filtersQs}`,
          auth,
          effectiveCompanyId
        );
        if (!cancelled && res?.data) setFilters((s) => ({ ...s, ...res.data }));
      } catch {
        /* non-fatal — the page still works with free-text / empty pickers */
      }
    })();
    return () => { cancelled = true; };
  }, [user, effectiveCompanyId, filtersQs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Merchant list is company-dependent.
  useEffect(() => { setDimFilters((s) => ({ ...s, merchantId: "" })); }, [dimFilters.companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Summary + timeseries.
  useEffect(() => {
    if (!user || !effectiveCompanyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const [s, t] = await Promise.all([
          makeApiRequest("get", `${apiRoutes.analyticsAuthPerformanceSummary}?${scopeQs}`, auth, effectiveCompanyId),
          makeApiRequest("get", `${apiRoutes.analyticsAuthPerformanceTimeseries}?${scopeQs}`, auth, effectiveCompanyId),
        ]);
        if (cancelled) return;
        setSummary(s?.data?.totals || null);
        setRange(s?.data?.range || null);
        setByCompany(s?.data?.byCompany || []);
        setSeries(
          (t?.data?.series || []).map((d) => ({
            ...d,
            date: String(d.transaction_date ?? "").slice(0, 10),
            auth_rate: authRate(d),
          }))
        );
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, effectiveCompanyId, scopeQs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Report — refetches on grouping change too.
  useEffect(() => {
    if (!user || !effectiveCompanyId) return;
    if (!groupBy.length) { setReport(EMPTY_REPORT); return; }
    let cancelled = false;
    (async () => {
      setReportLoading(true);
      setReportError(false);
      try {
        const qs = buildQS({ startDate, endDate, dateBasis, dimFilters, flags, groupBy });
        const res = await makeApiRequest("get", `${apiRoutes.analyticsAuthPerformanceReport}?${qs}`, auth, effectiveCompanyId);
        if (cancelled) return;
        const rows = res?.data?.rows || [];
        // totalGroups is the TRUE grouped cardinality (COUNT(*) OVER (), pre-LIMIT).
        // Falls back to the returned length against an older API, which reads as
        // "not truncated" — the pre-SOUND-600 behaviour, not a silent wrong answer.
        const totalGroups = Number(res?.data?.totalGroups ?? rows.length);
        setReport({
          rows,
          rowCount: rows.length,
          totalGroups,
          truncated: res?.data?.truncated ?? totalGroups > rows.length,
          groupBy: res?.data?.groupBy || groupBy,
        });
      } catch {
        if (!cancelled) { setReportError(true); setReport(EMPTY_REPORT); }
      } finally {
        if (!cancelled) setReportLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, effectiveCompanyId, scopeQs, groupBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the sort key valid when the grouping changes.
  useEffect(() => {
    if (sort.key !== "attempts" && sort.key !== "approved" && sort.key !== "auth_rate" && !groupBy.includes(sort.key)) {
      setSort({ key: "attempts", dir: "desc" });
    }
  }, [groupBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetFilters = useCallback(() => {
    setDimFilters(EMPTY_DIM_FILTERS);
    setFlags(EMPTY_FLAGS);
  }, []);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  const arrow = (key) => (sort.key === key ? (sort.dir === "desc" ? " ↓" : " ↑") : "");

  const sortedRows = useMemo(() => {
    const rows = [...report.rows];
    const { key, dir } = sort;
    const mult = dir === "desc" ? -1 : 1;
    rows.sort((a, b) => {
      const av = key === "auth_rate" ? authRate(a) : a[key];
      const bv = key === "auth_rate" ? authRate(b) : b[key];
      if (typeof av === "number" || typeof bv === "number" || key === "attempts" || key === "approved") {
        const an = av === null || av === undefined ? -Infinity : Number(av);
        const bn = bv === null || bv === undefined ? -Infinity : Number(bv);
        return (an - bn) * mult;
      }
      return String(av ?? "").localeCompare(String(bv ?? "")) * mult;
    });
    return rows;
  }, [report.rows, sort]);

  const visibleRows = useMemo(() => sortedRows.slice(0, MAX_RENDER_ROWS), [sortedRows]);

  // ── caveats that apply to the current selection ──
  const activeGroupCaveats = useMemo(() => {
    const keys = new Set();
    groupBy.forEach((k) => { if (DIM_BY_KEY[k]?.caveat) keys.add(DIM_BY_KEY[k].caveat); });
    // Filtering on a cascade also earns the cascade note.
    if (dimFilters.cascadeId) keys.add("cascade");
    if (flags.isRerunnerAttempt !== "") keys.add("rerunner");
    if (flags.isCascadeAttempt !== "" || flags.isInitial !== "") keys.add("cascade_overlap");
    if (dimFilters.cardBin || dimFilters.binFrom || dimFilters.binTo) keys.add("card_bin");
    if (dimFilters.issuerName) keys.add("issuer_name");
    if (dimFilters.brand) keys.add("brand");
    return [...keys];
  }, [groupBy, dimFilters, flags]);

  // The overlap caveat escalates when both flags are grouped at once.
  const overlapEscalated = groupBy.includes("is_cascade_attempt") && groupBy.includes("is_initial");

  // Derived-cascade rows present in the current report?
  const hasDerivedRows = useMemo(
    () => report.rows.some((r) => String(r.cascade_source || "").toLowerCase() === "derived"),
    [report.rows]
  );

  // Cascade grouped but source not — the analyst can't see which is which.
  const cascadeWithoutSource =
    (groupBy.includes("cascade_id") || groupBy.includes("is_cascade_attempt") || groupBy.includes("is_cascade_retry")) &&
    !groupBy.includes("cascade_source");

  // Rerunner grouped without company — a structural 0 is indistinguishable.
  const rerunnerWithoutCompany = groupBy.includes("is_rerunner_attempt") && !groupBy.includes("company_id");

  // Filtering on ET but bucketing on the UTC date (or vice-versa).
  const basisMismatch =
    (dateBasis === "et" && groupBy.includes("transaction_date") && !groupBy.includes("transaction_date_et")) ||
    (dateBasis === "utc" && groupBy.includes("transaction_date_et") && !groupBy.includes("transaction_date"));

  // Live issuer coverage, when issuer is one of the grouped dimensions.
  const issuerCoverage = useMemo(() => {
    if (!groupBy.includes("issuer_name") || !report.rows.length) return null;
    let total = 0, resolved = 0;
    report.rows.forEach((r) => {
      const a = Number(r.attempts || 0);
      total += a;
      if (!isUnknown(r.issuer_name)) resolved += a;
    });
    if (!total) return null;
    return { total, resolved, pct: (resolved / total) * 100 };
  }, [groupBy, report.rows]);

  const addDim = (key) => setGroupBy((g) => (g.includes(key) || g.length >= MAX_GROUP_BY ? g : [...g, key]));

  const brand = cssVar("--brand-600", 1) || cssVar("--brand", 1) || "#5affa2";
  const blue = "hsl(217 91% 60%)";

  const th = "px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider";
  const thSort = `${th} cursor-pointer select-none`;
  const thNum = `${th} text-right cursor-pointer select-none`;

  return (
    <div>
      <PageHeader
        title="Authorization Performance"
        subtitle="Attempts and approvals only — auth rate is always recomputed from the two counts. Group by any combination of dimensions to build a report."
        // Range and date basis deliberately NOT repeated here — they are settings, and
        // they live in the control panel below with the inputs that change them. These
        // chips are for what the DATA says, not what was asked for.
        meta={[
          ...(summary ? [
            { label: "Companies", value: fmtInt(summary.companies) },
            { label: "Merchants", value: fmtInt(summary.merchants) },
            { label: "Days", value: fmtInt(summary.days) },
          ] : []),
        ]}
      />

      <div className="container py-6 space-y-6">
        {/* Range + date basis */}
        <div className="surface rounded-xl ring-1 ring-border p-4 flex flex-col lg:flex-row lg:items-end gap-6">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Range</label>
            <div className="flex items-center gap-2">
              <Segmented
                value={dateMode === "preset" ? rangeDays : dateMode}
                onChange={(v) =>
                  typeof v === "number"
                    ? (setDateMode("preset"), setRangeDays(v))
                    : setDateMode(v)
                }
                options={[
                  ...RANGE_PRESETS.map((p) => ({ value: p.days, label: p.label })),
                  ...CALENDAR_PRESETS,
                  { value: "custom", label: "Custom", title: "Pin both ends — needed to match a CheckoutChamp report window" },
                ]}
              />
            </div>

            {dateMode === "custom" && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={customStart}
                  max={customEnd || undefined}
                  onChange={(e) => setCustomStart(e.target.value)}
                  aria-label="Start date"
                  className="surface rounded-md ring-1 ring-border px-2 py-1 text-xs text-foreground"
                />
                <span className="text-xs text-muted-foreground">→</span>
                <input
                  type="date"
                  value={customEnd}
                  min={customStart || undefined}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  aria-label="End date"
                  className="surface rounded-md ring-1 ring-border px-2 py-1 text-xs text-foreground"
                />
                {!customValid && (
                  <span className="text-[11px] text-destructive">
                    Start must be on or before end — showing the {rangeDays}d preset until both are valid.
                  </span>
                )}
              </div>
            )}

            {dateMode === "custom" && customValid && dateBasis !== "et" && (
              <p className="mt-2 text-[11px] text-amber-500 max-w-md">
                For a CheckoutChamp comparison switch the basis to <strong>ET</strong> — CheckoutChamp
                reports in Eastern, and ~13.6% of attempts land on a different calendar day under UTC.
              </p>
            )}

            {/* The resolved window, next to the control that sets it rather than floated
                to the far side of the panel. Shows the inclusive day count, because the
                API's BETWEEN is inclusive and the arithmetic is otherwise easy to
                mis-read. Flags an incomplete trailing day, which is the single most
                common reason a total drifts from a vendor report. */}
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <span className="text-muted-foreground">
                <CalendarRange className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                {startDate} → {endDate}
              </span>
              <span className="text-foreground/70">
                {inclusiveDays} {inclusiveDays === 1 ? "day" : "days"}
              </span>
              <span className="uppercase text-muted-foreground">{dateBasis}</span>
              {useCustom && <span className="text-muted-foreground">· custom</span>}
              {calendarRange && (
                <span
                  className="uppercase text-muted-foreground"
                  title={`Anchored to the ${dateBasis.toUpperCase()} calendar — the basis you are reading`}
                >
                  · {dateMode}
                </span>
              )}
              {endsToday && (
                <span
                  className="text-amber-500"
                  title="Today is still in progress, so the last day is partial. Vendor reports pulled earlier will show fewer attempts. End the range yesterday for a like-for-like comparison."
                >
                  · today partial
                </span>
              )}
              {/* A calendar-anchored period can legitimately be empty — MTD on the 1st
                  before the day's data lands is the common case. Say so, because an
                  empty table with no explanation reads as a broken page. */}
              {!loading && summary && Number(summary.days) === 0 && (
                <span
                  className="text-amber-500"
                  title="The resolved window contains no days with data yet. For MTD on the 1st of a month this is expected — the period has only just started."
                >
                  · no data in this period
                </span>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Date basis</label>
            <Segmented
              value={dateBasis}
              onChange={setDateBasis}
              options={[
                { value: "utc", label: "UTC", title: "Default. Consistent with every other mart." },
                { value: "et", label: "ET", title: "Reconciles against the CheckoutChamp UI." },
              ]}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              ET reconciles against CheckoutChamp; UTC matches every other mart.
            </p>
          </div>
        </div>

        {basisMismatch && (
          <Callout
            tone="warning"
            title="Date basis and date dimension don't match"
            body={
              dateBasis === "et"
                ? "You're filtering on the ET basis but bucketing by transaction_date (UTC). Group by Date (ET) so the buckets line up with the filter."
                : "You're filtering on the UTC basis but bucketing by transaction_date_et. Group by Date (UTC), or switch the basis to ET."
            }
            action={
              <button
                type="button"
                onClick={() => addDim(dateBasis === "et" ? "transaction_date_et" : "transaction_date")}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md ring-1 ring-warning/40 text-warning hover:bg-warning/10"
              >
                <Plus className="h-3 w-3" /> Add {dateBasis === "et" ? "Date (ET)" : "Date (UTC)"}
              </button>
            }
          />
        )}

        {error && (
          <div className="surface rounded-xl ring-1 ring-border p-8 text-center text-sm text-destructive">
            Failed to load authorization performance.
          </div>
        )}

        {/* KPI strip */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="surface rounded-xl ring-1 ring-border p-5">
                <Skeleton.Rect className="h-5 w-20" />
                <Skeleton.Rect className="h-3 w-12 mt-2" />
              </div>
            ))}
          </div>
        ) : summary && (
          <PageKpis
            columns={{ base: 2, md: 4, lg: 4 }}
            items={[
              { label: "Attempts", value: Number(summary.attempts) || 0, icon: <CreditCard />, tone: "brand" },
              { label: "Approved", value: Number(summary.approved) || 0, icon: <CheckCircle />, tone: "success" },
              // auth_rate is 0–100; PageKpis "percent" multiplies by 100.
              {
                label: "Auth Rate",
                value: (authRate(summary) ?? Number(summary.auth_rate) ?? 0) / 100,
                format: "percent",
                icon: <Percent />,
                tone: "info",
              },
              // COUNT(DISTINCT date) server-side — days that actually HAVE data, which is not
              // the same as the range length. Labelling it "Days in range" made a
              // zero-traffic day look like a bug.
              { label: "Days with data", value: Number(summary.days) || 0, icon: <CalendarRange />, tone: "neutral" },
            ]}
          />
        )}

        {/* Timeseries */}
        {!loading && series.length > 0 && (
          <ChartCard
            title="Auth Rate over time"
            subtitle={`Daily, recomputed from approved / attempts. Bars are attempts (right axis). ${(range?.dateBasis || dateBasis).toUpperCase()} date basis.`}
            height={280}
          >
            <ComposedChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={cssVar("--border", 0.5)} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
              <YAxis yAxisId="rate" domain={[0, 100]} tick={{ fontSize: 11 }} width={38} />
              <YAxis
                yAxisId="vol"
                orientation="right"
                tick={{ fontSize: 11 }}
                width={56}
                tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : fmtInt(v))}
              />
              <Tooltip
                contentStyle={tooltipStyle()}
                formatter={(v, n) => (n === "Auth rate" ? [fmtPct(v), n] : [fmtInt(v), n])}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="vol" name="Attempts" dataKey="attempts" fill={blue} fillOpacity={0.35} isAnimationActive={false} />
              <Line yAxisId="rate" name="Auth rate" type="monotone" dataKey="auth_rate" stroke={brand} strokeWidth={2} dot={false} isAnimationActive={false} />
            </ComposedChart>
          </ChartCard>
        )}

        {/* Filters */}
        <FilterPanel
          filters={filters}
          dimFilters={dimFilters}
          setDimFilters={setDimFilters}
          flags={flags}
          setFlags={setFlags}
          onReset={resetFilters}
        />

        {/* Group-by picker + the caveats it earns */}
        <div className="surface rounded-xl ring-1 ring-border p-5 space-y-4">
          <GroupByPicker groupBy={groupBy} onChange={setGroupBy} />

          {activeGroupCaveats.length > 0 && (
            <div className="space-y-2">
              {activeGroupCaveats.map((k) => {
                const c = CAVEATS[k];
                if (!c) return null;
                const escalate = k === "cascade_overlap" && overlapEscalated;
                return (
                  <Callout
                    key={k}
                    tone={escalate ? "warning" : c.tone}
                    title={escalate ? `${c.title} — and you are grouping by BOTH right now` : c.title}
                    body={c.body}
                    action={
                      k === "cascade_overlap" && !groupBy.includes("is_cascade_retry") ? (
                        <button
                          type="button"
                          onClick={() => addDim("is_cascade_retry")}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md ring-1 ring-warning/40 text-warning hover:bg-warning/10"
                        >
                          <Plus className="h-3 w-3" /> Add Cascade Retry
                        </button>
                      ) : k === "rerunner" && rerunnerWithoutCompany ? (
                        <button
                          type="button"
                          onClick={() => addDim("company_id")}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md ring-1 ring-warning/40 text-warning hover:bg-warning/10"
                        >
                          <Plus className="h-3 w-3" /> Add Company
                        </button>
                      ) : null
                    }
                  />
                );
              })}
            </div>
          )}

          {cascadeWithoutSource && (
            <Callout
              tone="warning"
              title="Cascade grouped without Cascade Source — you can't tell vendor truth from inference"
              body="CheckoutChamp cascade is native (its own field, reproduces CC's Cascade Performance screen). Phoenix cascade is derived by us with no vendor figure to verify against. Without cascade_source in the grouping, both are in the same rows at the same apparent confidence."
              action={
                <button
                  type="button"
                  onClick={() => addDim("cascade_source")}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md ring-1 ring-warning/40 text-warning hover:bg-warning/10"
                >
                  <Plus className="h-3 w-3" /> Add Cascade Source
                </button>
              }
            />
          )}
        </div>

        {/* Chart over the report's current result set. Optional, and only offered
            once there is something to chart — the chart types themselves are gated
            on the shape of groupBy + measure inside ReportChart. */}
        {groupBy.length > 0 && !reportLoading && report.rows.length > 0 && (
          showChart ? (
            <ReportChart
              rows={report.rows}
              groupBy={report.groupBy}
              dateDims={DATE_DIMS}
              dimLabel={(k) => DIM_BY_KEY[k]?.label || k}
              truncated={report.truncated}
              totalGroups={report.totalGroups}
              onHide={() => setShowChart(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setShowChart(true)}
              className="btn-secondary inline-flex items-center gap-2 text-xs self-start w-fit"
            >
              <BarChart3 className="h-3.5 w-3.5" /> Show chart
            </button>
          )
        )}

        {/* Report */}
        <div className="surface rounded-xl ring-1 ring-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Report{groupBy.length > 0 && <span className="text-muted-foreground font-normal"> — {groupBy.map((k) => DIM_BY_KEY[k]?.label || k).join(" × ")}</span>}
              </h3>
              <p className="text-xs text-muted-foreground">
                {reportLoading
                  ? "Building…"
                  : `${fmtInt(report.rowCount)} row${report.rowCount === 1 ? "" : "s"}${
                      report.truncated
                        ? ` of ${fmtInt(report.totalGroups)} groups — server capped at the heaviest ${fmtInt(report.rowCount)} by attempts`
                        : ""
                    }${
                      sortedRows.length > MAX_RENDER_ROWS
                        ? ` · showing first ${fmtInt(MAX_RENDER_ROWS)} — the CSV export includes all of them`
                        : ""
                    }`}
              </p>
              {/* The CSV is the whole RETURNED set, which is not the whole result
                  when the server capped it. Saying "includes all of them" above
                  would otherwise be a lie at high cardinality. */}
              {report.truncated && !reportLoading && (
                <p className="mt-1 text-[11px] text-amber-500">
                  Narrow the range or add a filter to see the rest — the export covers the returned rows only.
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <span title="Rows below this many attempts keep their rate, but it renders dimmed and tagged. Nothing is ever hidden. Set 0 to disable.">
                  Low-sample floor
                </span>
                <input
                  className="input w-16 py-1 text-xs"
                  inputMode="numeric"
                  value={lowSampleFloor}
                  onChange={(e) => setLowSampleFloor(Math.max(0, Number(e.target.value.replace(/\D/g, "")) || 0))}
                />
              </label>
              <button
                type="button"
                disabled={!report.rows.length}
                onClick={() => exportReportCsv({ rows: sortedRows, groupBy: report.groupBy, startDate, endDate, dateBasis })}
                className="btn-secondary inline-flex items-center gap-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download className="h-3.5 w-3.5" /> Export CSV
              </button>
            </div>
          </div>

          {hasDerivedRows && (
            <div className="px-5 py-2 bg-warning/10 border-b border-warning/20 text-[11px] text-foreground/80 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
              <span>
                Rows marked <span className={clsx(CHIP, "bg-warning/15 text-warning ring-warning/30")}>derived</span> are
                Phoenix cascades we inferred — dimmed on purpose. No vendor figure exists to verify them. Native rows are
                CheckoutChamp's own field.
              </span>
            </div>
          )}

          {issuerCoverage && (
            <div className="px-5 py-2 bg-info/10 border-b border-info/20 text-[11px] text-foreground/80 flex items-center gap-2">
              <Info className="h-3.5 w-3.5 text-info shrink-0" />
              <span>
                Issuer coverage in this report: <strong>{fmtPct(issuerCoverage.pct)}</strong> of{" "}
                {fmtInt(issuerCoverage.total)} attempts resolved to a named issuer. The rest are UNKNOWN — non-card
                rails have no issuer, and Amex / Discover are absent from our BIN table.
              </span>
            </div>
          )}

          {reportError ? (
            <div className="p-8 text-center text-sm text-destructive">Failed to build the report.</div>
          ) : !groupBy.length ? (
            <div className="p-12 text-center">
              <Layers className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                Pick at least one dimension above to build a report.
              </p>
            </div>
          ) : reportLoading ? (
            <div className="p-5 space-y-2">
              {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton.Rect key={i} className="h-6 w-full" />)}
            </div>
          ) : !report.rows.length ? (
            <div className="p-12 text-center">
              <BarChart3 className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">No rows for this range, filter set, and grouping.</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[640px]">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="bg-card sticky top-0 z-10">
                  <tr>
                    {report.groupBy.map((k) => (
                      <th key={k} className={thSort} onClick={() => toggleSort(k)}>
                        <span className="inline-flex items-center gap-1">
                          {DIM_BY_KEY[k]?.label || k}
                          {DIM_BY_KEY[k]?.caveat && (
                            <AlertTriangle
                              className={clsx(
                                "h-3 w-3",
                                CAVEATS[DIM_BY_KEY[k].caveat]?.tone === "warning" ? "text-warning" : "text-info"
                              )}
                              title={CAVEATS[DIM_BY_KEY[k].caveat]?.title}
                            />
                          )}
                          {arrow(k)}
                        </span>
                      </th>
                    ))}
                    <th className={thNum} onClick={() => toggleSort("attempts")}>Attempts{arrow("attempts")}</th>
                    <th className={thNum} onClick={() => toggleSort("approved")}>Approved{arrow("approved")}</th>
                    <th className={thNum} onClick={() => toggleSort("auth_rate")} title="approved / attempts, recomputed">
                      Auth Rate{arrow("auth_rate")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {visibleRows.map((r, i) => {
                    const attempts = Number(r.attempts || 0);
                    const rate = authRate(r);
                    const lowSample = lowSampleFloor > 0 && attempts < lowSampleFloor;
                    const derived = String(r.cascade_source || "").toLowerCase() === "derived";
                    return (
                      <tr
                        key={`${report.groupBy.map((k) => r[k]).join("|")}|${i}`}
                        className={clsx("hover:bg-muted/40", derived && "italic")}
                      >
                        {report.groupBy.map((k) => (
                          <td key={k} className="px-3 py-2">
                            <DimCell dimKey={k} row={r} />
                          </td>
                        ))}
                        <td className="px-3 py-2 text-right tabular-nums">{fmtInt(attempts)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.approved)}</td>
                        <td
                          className={clsx(
                            "px-3 py-2 text-right tabular-nums",
                            lowSample || derived ? "text-muted-foreground/70" : "font-medium text-foreground"
                          )}
                          title={
                            lowSample
                              ? `Only ${fmtInt(attempts)} attempts — below the ${lowSampleFloor}-attempt floor. The rate is real but not meaningful.`
                              : derived
                              ? "Derived cascade (Phoenix) — inferred, not vendor-reported."
                              : undefined
                          }
                        >
                          {fmtPct2(rate)}
                          {lowSample && (
                            <span className="ml-1 text-[10px] text-warning" title="Low sample">
                              n={fmtInt(attempts)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {lowSampleFloor > 0 && report.rows.length > 0 && (
            <div className="px-5 py-2 border-t border-border text-[11px] text-muted-foreground">
              Rates from fewer than {lowSampleFloor} attempts are dimmed and tagged{" "}
              <span className="text-warning">n=…</span>. Nothing is filtered out — every BIN × day combination stays
              sliceable by design.
            </div>
          )}
        </div>

        {/* By-company rollup (cross-company view only) */}
        {!loading && !dimFilters.companyId && byCompany.length > 0 && (
          <div className="surface rounded-xl ring-1 ring-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">By Company</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-card">
                  <tr>
                    <th className={th}>Company</th>
                    <th className={`${th} text-right`}>Attempts</th>
                    <th className={`${th} text-right`}>Approved</th>
                    <th className={`${th} text-right`}>Auth Rate</th>
                    <th className={`${th} text-right`}>Rerunner</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {byCompany.map((c) => (
                    <tr
                      key={c.company_id}
                      className="hover:bg-muted/40 cursor-pointer"
                      onClick={() => setDimFilters((s) => ({ ...s, companyId: c.company_id }))}
                    >
                      <td className="px-3 py-2 font-medium text-foreground">{c.company_id}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(c.attempts)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(c.approved)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtPct2(authRate(c))}</td>
                      <td className="px-3 py-2 text-right">
                        {RERUNNER_CLIENTS.has(String(c.company_id).toLowerCase()) ? (
                          <span className={clsx(CHIP, "bg-brand/15 text-foreground ring-brand/30")}>enabled</span>
                        ) : (
                          <span
                            className={clsx(CHIP, "bg-muted text-muted-foreground/70 ring-border italic")}
                            title="No rerunner for this client — is_rerunner_attempt is structurally 0, not a measured 0%."
                          >
                            n/a
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && series.length === 0 && (
          <div className="surface rounded-xl ring-1 ring-border p-12 text-center">
            <BarChart3 className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No attempts for the selected range and filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default requireAuth(AuthPerformanceInner);
